import { useState } from 'react';
import { AppBlock, AppBlockAppealStatus } from '@back4app2/sdk';
import { ReactComponent as CircleExclamationSVG } from '../../assets/images/circle-exclaimation-icon.svg';
import AppealBlockModal from '../AppealBlockModal';
import Button from '../Button';
import CustomerErrorMessage from './CustomErrorMessage';

/**
 * What a customer sees when the analyzer blocked their app.
 *
 * The copy names the policy categories — which are already public — and never
 * a file, a line, a pattern or the reasoning. An abuser reading this learns
 * "do not run a tunnel", which they knew; they learn nothing about what to
 * rename.
 *
 * It also says the app is offline and that the data is not. Those are the two
 * things a legitimate customer panics about, and not answering them is what
 * turns a block into a support ticket regardless of the appeal button.
 */
const SecurityBlockErrorMessage = ({
  appId,
  block,
  userEmail,
}: {
  appId: string;
  block: AppBlock;
  userEmail?: string;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  // Held locally rather than refetched: submitting an appeal writes to
  // app_block_appeals, not to apps, so the app subscription this page reads
  // does not fire. The server is the source of truth on the next load; this is
  // only so the customer is not left looking at the button they just used.
  const [justSubmitted, setJustSubmitted] = useState(false);

  const appealStatus = justSubmitted
    ? AppBlockAppealStatus.PENDING
    : block.appealStatus;

  const submittedAt = block.appealSubmittedAt
    ? new Date(block.appealSubmittedAt).toLocaleDateString()
    : '';

  return (
    <>
      <CustomerErrorMessage
        type="error"
        icon={
          <CircleExclamationSVG
            width="27px"
            height="26px"
            className="text-error-red flex-none"
          />
        }
        headerText="Web deployment blocked by a security review"
        description={
          <>
            {/* No list of categories. Saying which ones we detect is the
                half of the policy that is useful to somebody working around
                it, and the customer who genuinely needs to know is told to
                ask — a person answers. */}
            <div className="text-sm mb-4">
              This web deployment was stopped before it was built. An automated
              security check found code in this repository that matches activity
              that isn&apos;t allowed on Back4app Containers.
            </div>

            <div className="text-sm mb-6">
              Your web application has been taken offline while this is
              reviewed. Your code, environment variables and data are untouched.
            </div>

            {appealStatus === AppBlockAppealStatus.AVAILABLE ? (
              <>
                <div className="text-sm mb-6">
                  If you believe this is a mistake, you can send{' '}
                  <strong>one</strong> review request to our team. Tell us what
                  this app does and what the flagged code is for.
                </div>
                <Button type="primary" onClick={() => setModalOpen(true)}>
                  Request a review
                </Button>
              </>
            ) : null}

            {appealStatus === AppBlockAppealStatus.PENDING ? (
              <div className="text-sm">
                <div className="font-bold mb-2">Under review</div>
                We received your review request
                {submittedAt ? ` on ${submittedAt}` : ''}.
                Your app stays offline until the review is finished. We&apos;ll
                email you with the outcome. You don&apos;t need to send anything
                else.
              </div>
            ) : null}

            {appealStatus === AppBlockAppealStatus.REJECTED ? (
              <div className="text-sm">
                We reviewed your request and the block stands.
                {block.appealResolutionMessage
                  ? ` ${block.appealResolutionMessage}`
                  : ''}
              </div>
            ) : null}

            {/* The allowance is spent, or this was never an analyzer block.
                Falls back to the ordinary support route rather than offering a
                button that would fail. */}
            {appealStatus === AppBlockAppealStatus.UNAVAILABLE ? (
              <div className="text-sm">
                Please contact our support team if you have any questions.
              </div>
            ) : null}
          </>
        }
      />

      {modalOpen ? (
        <AppealBlockModal
          appId={appId}
          contactEmail={userEmail}
          onClose={() => setModalOpen(false)}
          onSubmitted={() => {
            setModalOpen(false);
            setJustSubmitted(true);
          }}
        />
      ) : null}
    </>
  );
};

export default SecurityBlockErrorMessage;
